
import {
  Component, Watch, Mixins, Ref,
} from 'vue-property-decorator';
import AddressInputsCollapse from '@/features/shipment/components/createShipment/AddressInputsCollapse.vue';
import { packageTypes, signatureOptions } from '@/features/common/raw';
import { patchPreferences } from '@/features/settings/services/preferences.services';
import { VForm } from '@/features/common/types';
import { ShipFrom } from '@/features/shipment/types';
import { CityProvinceLookupMixin } from '@/features/shipment/components/mixins';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { UserPreferences } from '@/features/settings/types';
import { requiredRules, emailRules, minCharactersRules } from '@/features/common/helpers/validationRules';
import { isNumeric } from '@/features/common/helpers/strUtils';
import { PreferencesMixin } from './mixins';

@Component({
  components: {
    AddressInputsCollapse,
  },
})
export default class Shipping extends Mixins(
  PreferencesMixin,
  CityProvinceLookupMixin,
  ResponsivenessMixin,
) {
  @Ref('default-address-form') formDefaultAddress!: VForm;

  @Ref('default-address-required-form') formDefaultAddressRequired!: VForm;

  private requiredRules = requiredRules;

  private emailRules = emailRules;

  private minCharactersRules = minCharactersRules;

  private autoCollapseEnabled = true;

  private addFieldsFromFocused = false;

  private packageTypes = packageTypes;

  private signatureOptions = signatureOptions;

  private formShippingSettings = {
    packageType: '',
    signatureOption: '',
    reference: '',
    deliveryComments: '',
    isDimensionsMandatory: true,
    isReferenceMandatory: true,
    isShowSummaryPage: true,
    isShowImportSection: true,
    isAutoCollapseAddress: true,
    weightUnit: 'Lbs',
    dimensionUnit: 'Inches',
    isNewSecureshipWebsite: false,
    isAutomaticallyAddPackage: true,
    isHideShipperPhoneNumberFromLabel: true,
  }

  private formDefaultShipAddress: ShipFrom = {
    company: '',
    contact: '',
    phone: '',
    addr1: '',
    addr2: '',
    countryCode: '',
    postalCode: '',
    city: '',
    province: '',
    residential: false,
    emails: [],
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
  }

  private showSettingsAlert = {
    success: false,
    show: false,
  };

  private showShipAddressAlert = {
    success: false,
    show: false,
  };

  get defaultAddressEmail(): string {
    return this.formDefaultShipAddress.emails ? this.formDefaultShipAddress.emails[0] : '';
  }

  set defaultAddressEmail(val: string) {
    this.formDefaultShipAddress = {
      ...this.formDefaultShipAddress,
      emails: val ? [val] : [],
    };
  }

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get canSwitchToLegacy(): boolean {
    const userInfo = this.$store.getters.getUserInfo;
    if (!userInfo || !userInfo.account) return true;

    const account = userInfo.account.trim();
    if (!isNumeric(account)) return true;

    const accountNumber = parseInt(account, 10);
    return !(accountNumber >= 130000 && accountNumber <= 999999);
  }

  @Watch('isUserPreferences', { immediate: true })
  private populatePreferences(newValue: number) {
    if (!newValue) return;

    const {
      packageType,
      signatureOption,
      reference,
      comments,
      weightUnits,
      dimUnits,
      dimensionsMandatory,
      showSummaryPage,
      showImportSection,
      autoCollapseAddress,
      defaultAddress,
      useNewSite,
      isAutomaticallyAddPackage,
      isReferenceMandatory,
      isHideShipperPhoneNumberFromLabel,
    } = this.userPreferences.shippingSettings;

    this.formShippingSettings.packageType = packageType;
    this.formShippingSettings.signatureOption = signatureOption;
    this.formShippingSettings.reference = reference ?? '';
    this.formShippingSettings.deliveryComments = comments ?? '';
    this.formShippingSettings.weightUnit = weightUnits;
    this.formShippingSettings.dimensionUnit = dimUnits;
    this.formShippingSettings.isDimensionsMandatory = dimensionsMandatory;
    this.formShippingSettings.isReferenceMandatory = isReferenceMandatory;
    this.formShippingSettings.isShowSummaryPage = showSummaryPage;
    this.formShippingSettings.isShowImportSection = showImportSection;
    this.formShippingSettings.isAutoCollapseAddress = autoCollapseAddress;
    this.formShippingSettings.isNewSecureshipWebsite = !!useNewSite;
    this.formShippingSettings.isAutomaticallyAddPackage = isAutomaticallyAddPackage;
    this.formShippingSettings.isHideShipperPhoneNumberFromLabel = isHideShipperPhoneNumberFromLabel;
    this.autoCollapseEnabled = autoCollapseAddress;

    const userInfo = this.$store.getters.getUserInfo;

    if (defaultAddress) {
      this.formDefaultShipAddress = { ...defaultAddress };
    }

    const { emails } = this.formDefaultShipAddress;

    if (!emails || !emails.length || !emails[0]) {
      this.formDefaultShipAddress.emails = [userInfo.username];
    }
  }

  @Watch('formShippingSettings.isAutoCollapseAddress')
  private toggleAddressCollapsable(newValue: boolean) {
    this.autoCollapseEnabled = newValue;
  }

  private updateDefaultShipAddress() {
    const isValid = this.formDefaultAddress.validate();

    if (!isValid) {
      this.autoCollapseEnabled = false;
      return;
    }

    this.updateShipAddressPreference({
      path: 'defaultAddress',
      value: this.formDefaultShipAddress,
    });
    this.autoCollapseEnabled = this.formShippingSettings.isAutoCollapseAddress;
  }

  private async updateSettingsPreference({ path, value }: {
    path: string,
    value: unknown,
  }) {
    this.patchPreferenceInStore(`shippingSettings/${path}`, value);

    const response = await patchPreferences(this.userPreferences.id, [
      {
        path: `shippingSettings/${path}`,
        op: 'replace',
        value,
      },
    ]);

    this.showSettingsAlert = {
      show: true,
      success: !!response,
    };
  }

  private async updateShipAddressPreference({ path, value }: {
    path: string,
    value: unknown,
  }) {
    let isValid = true;

    isValid = this.formDefaultAddressRequired.validate();
    if (!isValid) return;

    isValid = this.formDefaultAddress.validate();
    if (!isValid) return;

    if (value === null || value === undefined) return;

    this.patchPreferenceInStore(`shippingSettings/${path}`, value);

    const response = await patchPreferences(this.userPreferences.id, [
      {
        path: `shippingSettings/${path}`,
        op: 'replace',
        value,
      },
    ]);

    this.showShipAddressAlert = {
      show: true,
      success: !!response,
    };
  }

  private handleAutofillCityProvince(data) {
    const fill = (city: string, province: string, residential: boolean): void => {
      this[data].city = city || this[data].city;
      this[data].province = province || this[data].province;
      this[data].residential = residential || this[data].residential;

      this.updateDefaultShipAddress();
    };

    this.autofillCityProvince(fill, data);
  }
}
